import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './ChatSidebar.css';

const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Floating Action Button Component
export function ChatFAB({ onClick }) {
  return (
    <button className="chat-fab" onClick={onClick} aria-label="Open Chat Assistant">
      <svg className="chat-fab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
      <span className="chat-fab-badge">AI</span>
    </button>
  );
}

// Simple Markdown renderer component
const MarkdownRenderer = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return null;
    
    // Convert markdown to HTML
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="md-h3">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="md-h2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="md-h1">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="md-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em class="md-italic">$1</em>')
      // Code blocks
      .replace(/```(.*?)```/gims, '<pre class="md-code-block"><code>$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code class="md-inline-code">$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="md-link" target="_blank" rel="noopener noreferrer">$1</a>')
      // Unordered lists
      .replace(/^\s*[-*+]\s+(.+)$/gim, '<li class="md-list-item">$1</li>')
      // Line breaks
      .replace(/\n/gim, '<br/>');
    
    // Wrap list items in ul
    html = html.replace(/(<li class="md-list-item">.*?<\/li>)/gis, '<ul class="md-list">$1</ul>');
    
    return html;
  };

  return (
    <div 
      className="markdown-content"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

function ChatSidebar({ experimentID, isOpen, onClose }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [vivaQuestions, setVivaQuestions] = useState([]);
  const [vivaIndex, setVivaIndex] = useState(0);
  const [isVivaMode, setIsVivaMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vivaAttempt, setVivaAttempt] = useState(1);
  const [vivaResults, setVivaResults] = useState([]); // { questionNum, result: 'correct'|'incorrect' }
  const [assistedMode, setAssistedMode] = useState(false);
  const messagesEndRef = useRef(null);
  const modalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [history]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Helper: build scorecard text from results
  function buildScorecard(results) {
    const lines = results.map((r) => 
      `Q${r.questionNum}: ${r.result === 'correct' ? '✅ Correct' : '❌ Incorrect'}`
    );
    const correct = results.filter(r => r.result === 'correct').length;
    const total = results.length;
    return `🎯 **Viva Complete!**\n\n**Scorecard:**\n${lines.join('\n')}\n\n**Score: ${correct}/${total}**\n\n${correct === total ? '🌟 Perfect score! Excellent work!' : correct >= total * 0.7 ? '👏 Well done! Great understanding of the concepts.' : '📚 Good effort! Review the concepts you missed and try again.'}`;
  }

  async function sendMessage() {
    if (!input.trim() || isLoading) return;
    const message = input.trim();
    setHistory((h) => [...h, { from: 'user', text: message }]);
    setInput('');
    setIsLoading(true);

    try {
      // if viva mode active
      if (isVivaMode && vivaQuestions.length) {
        const current = vivaQuestions[vivaIndex];
        const resp = await axios.post(`${apiBaseURL}/api/viva/answer`, {
          question: current.question, 
          userAnswer: message, 
          modelAnswer: current.answer,
          attempt: vivaAttempt
        });
        const body = resp.data;
        
        if (assistedMode) {
          // --- Assisted viva flow ---
          if (body.moveToNext === false) {
            // Got a hint — show it and let them try again
            setHistory((h) => [...h, { from: 'bot', text: `💡 **Hint:** ${body.hint}` }]);
            setVivaAttempt(2);
            setIsLoading(false);
            return;
          }
          
          // moveToNext is true — record result and advance
          const result = body.evaluation === 'correct' ? 'correct' : 'incorrect';
          const newResults = [...vivaResults, { questionNum: vivaIndex + 1, result }];
          setVivaResults(newResults);
          setHistory((h) => [...h, { from: 'bot', text: body.comment || `${result === 'correct' ? '✅' : '❌'} ${body.evaluation}` }]);
          
          if (vivaIndex + 1 < vivaQuestions.length) {
            // More questions
            setVivaIndex(vivaIndex + 1);
            setVivaAttempt(1);
            const nextQ = vivaQuestions[vivaIndex + 1];
            setTimeout(() => {
              setHistory((h) => [...h, { from: 'bot', text: `**Question ${vivaIndex + 2} of ${vivaQuestions.length}:**\n\n${nextQ.question}` }]);
            }, 400);
          } else {
            // All done — show scorecard
            setVivaQuestions([]);
            setVivaIndex(0);
            setVivaAttempt(1);
            setIsVivaMode(false);
            setAssistedMode(false);
            setTimeout(() => {
              setHistory((h) => [...h, { from: 'bot', text: buildScorecard(newResults) }]);
              setVivaResults([]);
            }, 400);
          }
        } else {
          // --- Legacy viva flow (unchanged) ---
          setHistory((h) => [...h, { from: 'bot', text: body.comment || JSON.stringify(body) }]);
          
          if (vivaIndex + 1 < vivaQuestions.length) {
            setVivaIndex(vivaIndex + 1);
            const nextQ = vivaQuestions[vivaIndex + 1];
            setTimeout(() => {
              setHistory((h) => [...h, { from: 'bot', text: `**Next Question:**\n\n${nextQ.question}` }]);
            }, 300);
          } else {
            setVivaQuestions([]);
            setVivaIndex(0);
            setIsVivaMode(false);
            setTimeout(() => {
              setHistory((h) => [...h, { from: 'bot', text: '🎯 **Viva Complete!**\n\nGreat job! You can now continue asking questions in normal mode.' }]);
            }, 300);
          }
        }
        setIsLoading(false);
        return;
      }

      // normal chat
      const resp = await axios.post(`${apiBaseURL}/api/chat`, {
        message, 
        history: history.map(h => `${h.from}: ${h.text}`), 
        experimentID
      });
      const body = resp.data;
      setHistory((h) => [...h, { from: 'bot', text: body.reply || JSON.stringify(body) }]);
    } catch (error) {
      console.error('Chat error:', error);
      setHistory((h) => [...h, { from: 'bot', text: '❌ Sorry, there was an error connecting to the chat service. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }

  async function startViva() {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resp = await axios.post(`${apiBaseURL}/api/viva/start`, { experimentID });
      const body = resp.data;
      if (body.questions) {
        setVivaQuestions(body.questions);
        setVivaIndex(0);
        setVivaAttempt(1);
        setVivaResults([]);
        setIsVivaMode(true);
        setAssistedMode(!!body.assistedMode);
        
        const totalQ = body.questions.length;
        const introText = body.assistedMode 
          ? `🎓 **Assisted Viva Mode Activated!**\n\nI'll ask you **${totalQ} questions** about the experiment. For each question:\n• You get **2 attempts**\n• If your first answer isn't quite right, I'll give you a **hint**\n• Then you can try again\n\nA scorecard will be shown at the end. Good luck!\n\n**Question 1 of ${totalQ}:**\n\n${body.questions[0].question}`
          : `🎓 **Viva Mode Activated!**\n\nI'll ask you ${totalQ} questions about the experiment. Answer each question to the best of your ability.\n\n**Question 1:**\n\n${body.questions[0].question}`;
        
        setHistory((h) => [...h, { from: 'bot', text: introText }]);
      }
    } catch (error) {
      console.error('Viva start error:', error);
      setHistory((h) => [...h, { from: 'bot', text: '❌ Failed to start viva mode. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }

  function switchToNormal() {
    setVivaQuestions([]);
    setVivaIndex(0);
    setVivaAttempt(1);
    setVivaResults([]);
    setIsVivaMode(false);
    setAssistedMode(false);
    setHistory((h) => [...h, { from: 'bot', text: '💬 **Switched to Normal Mode**\n\nYou can now ask questions about the experiment.' }]);
  }

  async function validateExperiment() {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const resp = await axios.post(`${apiBaseURL}/api/validate`, { experimentID });
      const body = resp.data;
      setHistory((h) => [...h, { from: 'bot', text: body.message || '✅ Validation complete.' }]);
    } catch (error) {
      console.error('Validate error:', error);
      setHistory((h) => [...h, { from: 'bot', text: '❌ Validation failed. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
  {/* No backdrop: keep the chat open without darkening the rest of the page */}
      
      {/* Modal */}
      <div className="chat-modal" ref={modalRef}>
        <aside className="chat-sidebar">
          <div className="chat-sidebar-header">
            <div className="header-title">
              <span className="title-icon">🧠</span>
              <span className="title-text">Lab Assistant</span>
            </div>
            <button 
              className="close-btn" 
              onClick={onClose}
              aria-label="Close chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="status-bar">
            <div className="status-badge">
              <span className="status-dot"></span>
              <span className="status-text">Online</span>
            </div>
          </div>

      <div className="chat-messages-container">
        {history.length === 0 && (
          <div className="welcome-card">
            <div className="welcome-icon">👋</div>
            <h3 className="welcome-title">Hello! I'm your Lab Assistant</h3>
            <p className="welcome-text">I can help you with:</p>
            <ul className="welcome-list">
              <li>💬 Understanding experiment theory and procedures</li>
              <li>🎓 Testing your knowledge with viva questions</li>
              <li>✅ Validating your experiment completion</li>
            </ul>
            <p className="welcome-cta">Ask a question or start a viva session!</p>
          </div>
        )}
        
        {history.map((m, i) => (
          <div key={i} className={`message-wrapper ${m.from}`}>
            <div className={`message-bubble ${m.from}`}>
              <MarkdownRenderer content={m.text} />
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message-wrapper bot">
            <div className="message-bubble bot loading">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-controls">
        <button 
          onClick={startViva}
          disabled={isVivaMode || isLoading}
          className={`control-btn ${isVivaMode ? 'active' : ''}`}
          title="Start Viva Mode"
        >
          🎓 {isVivaMode ? 'Viva Active' : 'Start Viva'}
        </button>
        <button 
          onClick={switchToNormal}
          disabled={!isVivaMode || isLoading}
          className="control-btn"
          title="Switch to Normal Mode"
        >
          💬 Ask Questions
        </button>
        <button 
          onClick={validateExperiment}
          disabled={isLoading}
          className="control-btn"
          title="Validate Experiment"
        >
          ✅ Validate
        </button>
      </div>

      <div className="chat-input-container">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isVivaMode ? "Type your answer..." : "Ask about the experiment..."}
          className="chat-input"
          rows="2"
          disabled={isLoading}
        />
        <button 
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          className="send-btn"
        >
          <svg className="send-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
    </aside>
      </div>
    </>
  );
}

export default ChatSidebar;
